import { useEffect, useState, useCallback, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import addIcon from "../assets/add-circle.svg";
import minusIcon from "../assets/minus-circle.svg";
import editIcon from "../assets/pen1.svg";
import fbIcon from "../assets/fb.svg";
import ytIcon from "../assets/yt.svg";
import inIcon from "../assets/in.svg";
import instaIcon from "../assets/insta.svg";
import { requestWithToken } from "../utils/apis";
import { Spinner } from "react-bootstrap";
import Help from "../components/Help";
import { bucketUrl, helpPagesData } from "../utils/helper";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  DndContext,
  pointerWithin,
  useDroppable,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  rectIntersection,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CommonModal from "../components/CommonModal";
import DeployTrigger from "../components/DeployTrigger";
import { toast } from "react-toastify";
import DataSortableTree from "../components/DNDSortableTree";

/* ---------------- Add/Edit modal ---------------- */
export function AddEditMenuItem({
  show,
  onClose,
  onSave,
  initialData,
  isChild,
}) {
  const [name, setName] = useState("");
  const [redirectTo, setRedirectTo] = useState("");
  const [icon, setIcon] = useState("");
  const [desc, setDesc] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      setName(initialData?.name || "");
      setRedirectTo(initialData?.redirect_to || "");
      setIcon(initialData?.extras?.icon || "");
      setDesc(initialData?.extras?.desc || "");
      setErrors({});
    }
  }, [show, initialData]);

  if (!show) return null;

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Please enter a name";
    if (!redirectTo.trim()) {
      newErrors.redirectTo = "Please enter a URL or slug";
    } else {
      try {
        if (redirectTo.includes("://")) new URL(redirectTo);
      } catch {
        newErrors.redirectTo = "Invalid URL format";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      name,
      redirect_to: redirectTo,
      extras: {
        icon,
        desc,
      },
    });
  };

  return (
    <CommonModal show={show} onClose={onClose} title="Custom Link">
      <h5>{initialData ? "Edit" : "Add"}</h5>

      <div className="frm-group">
        <label className="frm-label">Name</label>
        <input
          autoFocus
          type="text"
          className={`frm-input ${errors.name ? "error" : ""}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter name"
        />
        {errors.name && <div className="input-error">{errors.name}</div>}
      </div>

      <div className="frm-group">
        <label className="frm-label">Link (URL or slug)</label>
        <input
          type="text"
          className={`frm-input ${errors.redirectTo ? "error" : ""}`}
          value={redirectTo}
          // disabled={Boolean(initialData?.page_id)}
          onChange={(e) => setRedirectTo(e.target.value)}
          placeholder="https://example.com or /slug"
        />
        {errors.redirectTo && (
          <div className="input-error">{errors.redirectTo}</div>
        )}
      </div>

      {isChild && (
        <>
          <div className="frm-group">
            <label className="frm-label">Icon URL</label>
            <input
              type="text"
              className="frm-input"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Enter icon URL"
            />
          </div>
          <div className="frm-group">
            <label className="frm-label">Description</label>
            <input
              type="text"
              className="frm-input"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Enter description"
            />
          </div>
        </>
      )}

      <div className="card-footer-box" style={{ marginTop: 24 }}>
        <button className="btn-grey ms-auto me-3" onClick={onClose}>
          Cancel
        </button>
        <button className="btn-black" onClick={handleSubmit}>
          Save
        </button>
      </div>
    </CommonModal>
  );
}

/* ---------------- Validation schema ---------------- */
const menuItemSchema = Yup.object().shape({
  page_id: Yup.string().notRequired(),
  name: Yup.string().required(),
  redirect_to: Yup.string().when("page_id", (page_id, schema) =>
    page_id
      ? schema.notRequired()   // If it's a page, allow blank slug
      : schema.required("Link is required for custom menu items")
  ),
  children: Yup.array()
    .of(Yup.lazy(() => menuItemSchema))
    .notRequired(),
  extras: Yup.object({
    icon: Yup.string().notRequired(),
    desc: Yup.string().notRequired(),
  }).notRequired(),
});

const validationSchema = Yup.object({
  primarybutton: Yup.object({
    name: Yup.string(),
    redirect_to: Yup.string().notRequired(),
  }),
  secondarybutton: Yup.object({
    name: Yup.string(),
    redirect_to: Yup.string().notRequired(),
  }),
  socialmedia: Yup.array().of(
    Yup.object({
      name: Yup.string().notRequired(),
      redirect_to: Yup.string().notRequired(),
    })
  ),
  menu: Yup.array().of(menuItemSchema),
});

/* ---------------- Custom collision detection ---------------- */
const customCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);

  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  return rectIntersection(args);
};

/* ---------------- Improved Droppables ---------------- */
function DroppableAllPages({ children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: "ALL_PAGES",
    data: {
      type: "container",
      accepts: ["page"],
    },
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        backgroundColor: isOver ? "#f0f9ff" : undefined,
        border: isOver ? "2px solid #e5e7eb" : "1px solid #e5e7eb",
        minHeight: "150px",
        borderRadius: "8px",
        padding: "16px",
        transition: "all 0.2s ease",
      }}
    >
      {children}
    </div>
  );
}

function DroppableMenuPages({ children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: "MENU_PAGES",
    data: {
      type: "container",
      accepts: ["menu", "page"],
    },
  });
  return (
    <div
      ref={setNodeRef}
      className="cu-899"
      style={{
        backgroundColor: isOver ? "#f0f9ff" : undefined,
        border: isOver ? "2px solid #e5e7eb" : "1px solid #e5e7eb",
        minHeight: "150px",
        borderRadius: "8px",
        padding: "16px",
        transition: "all 0.2s ease",
      }}
    >
      {children}
    </div>
  );
}

function DroppableMenuItem({ id, children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: `droppable-menu-${id}`,
    data: {
      type: "container",
      accepts: ["menu", "page"],
    },
  });

  return (
    <div
      ref={setNodeRef}
      data-droppable={`droppable-${id}`}
      style={{
        backgroundColor: isOver ? "#eef6ff" : undefined,
        border: isOver ? "2px dashed #2563eb" : "none",
        borderRadius: "6px",
        padding: isOver ? "12px" : "0",
        transition: "all 0.15s ease",
        minHeight: isOver ? "70px" : "auto",
        position: "relative",
      }}
    >
      {isOver && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "25%",
            pointerEvents: "none",
            borderRight: "1px dashed rgba(0,0,0,0.06)",
          }}
        />
      )}
      {children}
    </div>
  );
}

/* ---------------- Sortable Components ---------------- */
function SortableMenuItem({
  item,
  children,
  onEdit,
  onRemove,
  isExpanded,
  onToggleExpand,
  hasChildren,
  isOver,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `menu-${item.__uid}`,
    data: {
      type: "item",
      item: item,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: isDragging ? "#f3f4f6" : "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="page-menu-item d-flex align-items-center"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(item.__uid);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              className="collapse-toggle"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                style={{ transform: isExpanded ? "rotate(90deg)" : "none" }}
              >
                <path
                  d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"
                  fill="currentColor"
                />
              </svg>
            </button>
          ) : (
            <div style={{ width: 24 }} />
          )}

          <span style={{ fontWeight: 500 }}>{item.name}</span>
          {!item.page_id && (
            <span style={{ color: "#888", fontSize: 10 }}> [link]</span>
          )}
        </div>

        <div className="d-flex align-items-center"></div>
      </div>

      {!isDragging && !isOver && children}
    </div>
  );
}

function DraggablePageItem({ page }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `page-${page._id || page.id}`,
    data: {
      type: "page",
      page: page,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    padding: "12px 16px",
    backgroundColor: isDragging ? "#f3f4f6" : "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    marginBottom: "8px",
    boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="page-menu-item"
    >
      {page.name}
    </div>
  );
}

/* ---------------- SortableMenuTree Component ---------------- */
function SortableMenuTree({
  items = [],
  overId,
  onEdit,
  onRemove,
  expandedUids,
  toggleExpand,
}) {
  const ids = items.map((i) => `menu-${i.__uid}`);

  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {items.map((item) => {
        const isOver = overId === `droppable-menu-${item.__uid}`;
        const isExpanded = expandedUids.has(item.__uid);
        const hasChildren = item.children && item.children.length > 0;

        return (
          <div key={item.__uid}>
            <DroppableMenuItem id={item.__uid} isOver={isOver}>
              <SortableMenuItem
                item={item}
                onEdit={onEdit}
                onRemove={onRemove}
                isExpanded={isExpanded}
                onToggleExpand={toggleExpand}
                hasChildren={hasChildren}
                isOver={isOver}
              />

              {hasChildren && isExpanded && (
                <div
                  className="submenu-items"
                  style={{
                    marginLeft: "28px",
                    borderLeft: "2px solid #e5e7eb",
                    paddingLeft: "20px",
                  }}
                >
                  <SortableMenuTree
                    items={item.children}
                    overId={overId}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    expandedUids={expandedUids}
                    toggleExpand={toggleExpand}
                  />
                </div>
              )}
            </DroppableMenuItem>
          </div>
        );
      })}
    </SortableContext>
  );
}

/* ---------------- Stable UID helpers ---------------- */
const makeUID = () =>
  `uid_${Date.now().toString(36)}_${Math.floor(Math.random() * 10000)}`;

function ensureUIDs(menu = []) {
  return (menu || []).map((item) => {
    const copy = { ...item };
    if (!copy.__uid) copy.__uid = makeUID();
    if (Array.isArray(copy.children) && copy.children.length) {
      copy.children = ensureUIDs(copy.children);
    } else {
      copy.children = copy.children || [];
    }
    return copy;
  });
}

/* ---------------- Simple Tree Operations ---------------- */
function collectAllMenuPageIds(menuItems = []) {
  let ids = [];
  (menuItems || []).forEach((item) => {
    if (item.page_id) ids.push(item.page_id);
    if (item.children?.length) ids = ids.concat(collectAllMenuPageIds(item.children));
  });
  return ids;
}

function addMenuType(menuArr, menuType) {
  return (menuArr || []).map((item) => ({
    ...item,
    menu_type: menuType,
    children: item.children ? addMenuType(item.children, menuType) : [],
  }));
}

// Deep clone function to prevent reference issues
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * findItemWithContext
 * returns { item, parent, parentArray, index } or null
 */
const findItemWithContext = (
  items,
  uid,
  parent = null,
  parentArray = null,
  index = -1
) => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.__uid === uid) {
      return { item, parent, parentArray: parentArray || items, index: i };
    }
    if (item.children?.length) {
      const found = findItemWithContext(item.children, uid, item, item.children, i);
      if (found) return found;
    }
  }
  return null;
};

/**
 * removeItemByUid
 * Purely returns a new tree *without* the item having given uid.
 */
const removeItemByUid = (items, uid) => {
  const result = [];
  for (const item of items) {
    if (item.__uid === uid) {
      continue;
    }
    if (item.children && item.children.length) {
      result.push({
        ...item,
        children: removeItemByUid(item.children, uid),
      });
    } else {
      result.push({ ...item });
    }
  }
  return result;
};

/**
 * insertBeforeUid
 * Inserts newItem before the element with targetUid somewhere in the tree.
 * Returns new tree (no mutation).
 */
const insertBeforeUid = (items, targetUid, newItem) => {
  const result = [];
  let inserted = false;

  for (const item of items) {
    if (item.__uid === targetUid) {
      result.push({ ...newItem });
      result.push({ ...item, children: item.children ? item.children : [] });
      inserted = true;
      continue;
    }

    if (item.children && item.children.length) {
      const updatedChildren = insertBeforeUid(item.children, targetUid, newItem);
      const childHasInserted = updatedChildren.some(
        (c) =>
          c.__uid === newItem.__uid ||
          (c.children && c.children.some((cc) => cc.__uid === newItem.__uid))
      );

      if (childHasInserted) {
        result.push({ ...item, children: updatedChildren });
        inserted = true;
      } else {
        result.push({
          ...item,
          children: item.children ? item.children.map((c) => ({ ...c })) : [],
        });
      }
    } else {
      result.push({ ...item });
    }
  }

  if (!inserted) {
    return items.map((it) => ({
      ...it,
      children: it.children ? it.children.map((c) => ({ ...c })) : [],
    }));
  }

  return result;
};

/**
 * addItemAsChild
 * Adds newItem as last child of parentUid (searches recursively) and returns new tree.
 */
const addItemAsChild = (items, parentUid, newItem) => {
  return items.map((item) => {
    if (item.__uid === parentUid) {
      return {
        ...item,
        children: [
          ...(item.children || []).map((c) => ({ ...c })),
          { ...newItem },
        ],
      };
    }
    if (item.children && item.children.length) {
      return {
        ...item,
        children: addItemAsChild(item.children, parentUid, newItem),
      };
    }
    return { ...item };
  });
};

// Function to remove __uid from menu items recursively
const removeUidsFromMenu = (menuItems = []) => {
  return menuItems.map(({ __uid, children, ...item }) => ({
    ...item,
    children: children ? removeUidsFromMenu(children) : [],
  }));
};

/* ---------------- Navigation component ---------------- */
export default function Navigation() {
  const [selectedMenuType, setSelectedMenuType] = useState(null);
  const [editMenuItem, setEditMenuItem] = useState({ item: null, path: [] });
  const [menuTypes, setMenuTypes] = useState({ loading: true, data: [] });
  const [pageList, setPageList] = useState({ loading: true, data: [] });
  const [selectedMenuData, setSelectedMenuData] = useState({
    loading: false,
    data: null,
  });
  const [activeModal, setActiveModal] = useState(null);
  const [menuState, setMenuState] = useState([]);

  // NEW: track expanded nodes as a Set for O(1) checks
  const [expandedUids, setExpandedUids] = useState(() => new Set());

  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemData, setDraggedItemData] = useState(null);
  const [overId, setOverId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [lastPointer, setLastPointer] = useState({ x: 0, y: 0 });

  // Track which nodes we auto-collapsed during a drag so we can restore them after drag ends
  const [collapsedDuringDrag, setCollapsedDuringDrag] = useState(() => new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // track pointer globally so we can compute relative position on drop
  useEffect(() => {
    const onPointerMove = (e) => {
      setLastPointer({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("pointermove", onPointerMove);
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, []);

  const formik = useFormik({
    initialValues: {
      primarybutton: { name: "", redirect_to: "" },
      secondarybutton: { name: "", redirect_to: "" },
      socialmedia: [],
      menu: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {

        setLoading(true);
        // Remove __uid from menu items before sending
        const menuWithoutUids = removeUidsFromMenu(menuState);
        const payload = {
          ...values,
          menu: addMenuType(menuWithoutUids, selectedMenuType),
        };

        if (
          !payload?.primarybutton?.name &&
          !payload?.primarybutton?.redirect_to
        ) {
          delete payload.primarybutton;
        } else {
          payload.primarybutton = {
            _id: payload.primarybutton._id,
            name: payload.primarybutton.name,
            redirect_to: payload.primarybutton.redirect_to,
            type: "primarybutton",
          };
        }

        if (
          !payload?.secondarybutton?.name &&
          !payload?.secondarybutton?.redirect_to
        ) {
          delete payload.secondarybutton;
        } else {
          payload.secondarybutton = {
            _id: payload.secondarybutton._id,
            name: payload.secondarybutton.name,
            redirect_to: payload.secondarybutton.redirect_to,
            type: "secondarybutton",
          };
        }

        const result = await requestWithToken(
          "PUT",
          `/api/menu/edit/navigations?menu_type=${selectedMenuType}`,
          payload
        );

        if (result?.status === 200) {
          toast.success(result?.data?.message || "Navigation saved");
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong, please try again");
      } finally {
        setLoading(false);
      }
    },
  });

  const usedPageIds = useMemo(() => collectAllMenuPageIds(menuState), [menuState]);
  const filteredPages = useMemo(
    () =>
      (pageList.data?.list || []).filter(
        (page) => !usedPageIds.includes(page._id)
      ),
    [pageList.data?.list, usedPageIds]
  );

  // Sync menuState when selectedMenuData changes
  useEffect(() => {
    if (selectedMenuData?.data?.menu) {
      const withUids = ensureUIDs(selectedMenuData.data.menu);
      setMenuState(withUids);
      formik.setFieldValue("menu", withUids);

      // Expand top-level items by default (optional).
      const initialExpanded = new Set(withUids.map((i) => i.__uid));
      setExpandedUids(initialExpanded);
    }
  }, [selectedMenuData?.data?.menu]);

  /* ---------- Expand/collapse helpers ---------- */
  const toggleExpand = useCallback((uid) => {
    setExpandedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const all = new Set();
    const traverse = (arr) => {
      for (const item of arr) {
        all.add(item.__uid);
        if (item.children?.length) traverse(item.children);
      }
    };
    traverse(menuState);
    setExpandedUids(all);
  }, [menuState]);

  const collapseAll = useCallback(() => {
    setExpandedUids(new Set());
  }, []);

  /* ---------- Drag handlers (updated to collapse during drag) ---------- */

  const handleDragStart = useCallback(
    ({ active }) => {
      setDraggedItem(active.id);

      if (active.id.startsWith("page-")) {
        const pageId = active.id.replace("page-", "");
        const page = pageList?.data?.list?.find((p) => p._id === pageId);
        setDraggedItemData(page?.name || "Item");
      } else if (active.id.startsWith("menu-")) {
        const uid = active.id.replace("menu-", "");
        const context = findItemWithContext(menuState, uid);
        setDraggedItemData(context?.item?.name || "Item");

        // AUTO-COLLAPSE: if the dragged item has children and is currently expanded, collapse it now
        if (context?.item?.children?.length && expandedUids.has(uid)) {
          setExpandedUids((prev) => {
            const next = new Set(prev);
            next.delete(uid);
            return next;
          });
          // remember we auto-collapsed this UID so we can restore it later
          setCollapsedDuringDrag((prev) => {
            const next = new Set(prev);
            next.add(uid);
            return next;
          });
        }
      }
    },
    [menuState, pageList, expandedUids]
  );

  const handleDragOver = useCallback(({ over }) => {
    setOverId(over?.id || null);
  }, []);

  const restoreCollapsedDuringDrag = useCallback(() => {
    if (!collapsedDuringDrag || collapsedDuringDrag.size === 0) return;
    setExpandedUids((prev) => {
      const next = new Set(prev);
      collapsedDuringDrag.forEach((uid) => next.add(uid));
      return next;
    });
    setCollapsedDuringDrag(new Set());
  }, [collapsedDuringDrag]);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      // restore any nodes we auto-collapsed during drag
      restoreCollapsedDuringDrag();

      setDraggedItem(null);
      setDraggedItemData(null);
      setOverId(null);

      if (!over) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData || !overData) return;

      // pointer coordinates from pointermove listener
      const pointerX = lastPointer.x;
      const pointerY = lastPointer.y;

      // Helper to get droppable element rect by target UID
      const getDroppableRect = (targetUid) => {
        try {
          const el = document.querySelector(
            `[data-droppable="droppable-${targetUid}"]`
          );
          return el ? el.getBoundingClientRect() : null;
        } catch {
          return null;
        }
      };

      // deep clone current state to work with (we will build newMenuState from this)
      const currentMenuState = deepClone(menuState);

      // Track if we need to expand a parent after drop
      let parentUidToExpand = null;

      /* ---------- PAGE dropped (new item) ---------- */
      if (activeData.type === "page") {
        const page = activeData.page;
        if (!page) return;

        const existingIds = new Set(collectAllMenuPageIds(currentMenuState));
        if (existingIds.has(page._id)) return;

        const newItem = {
          page_id: page._id,
          name: page.name,
          redirect_to: page.slug || "",
          children: [],
          extras: {},
          __uid: makeUID(),
          menu_id: null,
        };

        let newMenuState = currentMenuState;
        if (over.id === "MENU_PAGES") {
          newMenuState = [...currentMenuState, newItem];
        } else {
          const targetDroppableId = over.id.startsWith("droppable-menu-")
            ? over.id.replace("droppable-menu-", "")
            : over.id.startsWith("menu-")
              ? over.id.replace("menu-", "")
              : null;

          if (!targetDroppableId) {
            newMenuState = [...currentMenuState, newItem];
          } else {
            const rect = getDroppableRect(targetDroppableId);
            if (!rect) {
              newMenuState = addItemAsChild(currentMenuState, targetDroppableId, newItem);
              parentUidToExpand = targetDroppableId;
            } else {
              const relativeX = pointerX - rect.left;
              const threshold = rect.width * 0.25;
              if (relativeX <= threshold) {
                newMenuState = insertBeforeUid(currentMenuState, targetDroppableId, newItem);
              } else {
                newMenuState = addItemAsChild(currentMenuState, targetDroppableId, newItem);
                parentUidToExpand = targetDroppableId;
              }
            }
          }
        }

        setMenuState(newMenuState);
        formik.setFieldValue("menu", newMenuState);
      }

      /* ---------- MENU item moved ---------- */
      if (activeData.type === "item") {
        const sourceUid = activeData.item?.__uid;
        if (!sourceUid) return;

        const sourceContext = findItemWithContext(currentMenuState, sourceUid);
        if (!sourceContext) {
          return;
        }

        const sourceNodeFresh = deepClone(sourceContext.item);

        const itemsWithoutSource = removeItemByUid(currentMenuState, sourceUid);

        let newMenuState = itemsWithoutSource;

        if (over.id === "MENU_PAGES") {
          const itemWithNullMenuId = {
            ...sourceNodeFresh,
            menu_id: null,
            children: sourceNodeFresh.children ? sourceNodeFresh.children.map((child) => ({ ...child })) : [],
          };
          newMenuState = [...itemsWithoutSource, itemWithNullMenuId];
        } else {
          const targetDroppableId = over.id.startsWith("droppable-menu-")
            ? over.id.replace("droppable-menu-", "")
            : over.id.startsWith("menu-")
              ? over.id.replace("menu-", "")
              : null;

          if (!targetDroppableId) {
            const itemWithNullMenuId = {
              ...sourceNodeFresh,
              menu_id: null,
            };
            newMenuState = [...itemsWithoutSource, itemWithNullMenuId];
          } else {
            if (targetDroppableId === sourceUid) {
              setMenuState(itemsWithoutSource);
              formik.setFieldValue("menu", itemsWithoutSource);
              return;
            }

            const isDescendant = (parent, childUid) => {
              if (!parent.children) return false;
              for (const child of parent.children) {
                if (child.__uid === childUid) return true;
                if (isDescendant(child, childUid)) return true;
              }
              return false;
            };

            if (isDescendant(sourceNodeFresh, targetDroppableId)) {
              setMenuState(itemsWithoutSource);
              formik.setFieldValue("menu", itemsWithoutSource);
              return;
            }

            const rect = getDroppableRect(targetDroppableId);
            if (!rect) {
              newMenuState = addItemAsChild(itemsWithoutSource, targetDroppableId, sourceNodeFresh);
              parentUidToExpand = targetDroppableId;
            } else {
              const relativeX = pointerX - rect.left;
              const threshold = rect.width * 0.25;
              if (relativeX <= threshold) {
                const itemForInsertion = {
                  ...sourceNodeFresh,
                  menu_id: null,
                };
                newMenuState = insertBeforeUid(itemsWithoutSource, targetDroppableId, itemForInsertion);
              } else {
                newMenuState = addItemAsChild(itemsWithoutSource, targetDroppableId, sourceNodeFresh);
                parentUidToExpand = targetDroppableId;
              }
            }
          }
        }

        setMenuState(newMenuState);
        formik.setFieldValue("menu", newMenuState);
      }

      if (parentUidToExpand && !expandedUids.has(parentUidToExpand)) {
        setExpandedUids((prev) => new Set(prev).add(parentUidToExpand));
      }
    },
    [
      menuState,
      pageList,
      lastPointer,
      restoreCollapsedDuringDrag,
      expandedUids,
      formik,
    ]
  );

  // called when a drag is cancelled (user presses ESC or similar)
  const handleDragCancel = useCallback(() => {
    restoreCollapsedDuringDrag();
    setDraggedItem(null);
    setDraggedItemData(null);
    setOverId(null);
  }, [restoreCollapsedDuringDrag]);

  const handleEdit = useCallback((item) => {
    setEditMenuItem({ item, path: [] });
    setActiveModal("editMenuItem");
  }, []);

  function removeItemById(items, idToRemove) {
    return items
      .filter((item) =>
        item._id
          ? item._id !== idToRemove
          : item.page_id
            ? item.page_id !== idToRemove
            : item.id !== idToRemove
      )
      .map((item) => ({
        ...item,
        children: item.children?.length ? removeItemById(item.children, idToRemove) : [],
      }));
  }

  const handleAddCustomLink = () => {
    setEditMenuItem({ item: null, path: [] });
    setActiveModal("editMenuItem");
  };

  const handleMenuItemModalSave = (modalVals) => {
    let newMenus;
    if (editMenuItem.item) {
      const updateItemInTree = (items, targetUid, updates) => {
        return items.map((item) => {
          if (item.__uid === targetUid) {
            return { ...item, ...updates };
          }
          if (item.children) {
            return {
              ...item,
              children: updateItemInTree(item.children, targetUid, updates),
            };
          }
          return item;
        });
      };

      newMenus = updateItemInTree(menuState, editMenuItem.item.__uid, {
        name: modalVals.name,
        redirect_to: modalVals.redirect_to,
        extras: {
          icon: modalVals.extras?.icon || "",
          desc: modalVals.extras?.desc || "",
        },
      });
    } else {
      const newItem = {
        id: String(Date.now() + Math.random()),
        name: modalVals.name,
        redirect_to: modalVals.redirect_to,
        children: [],
        extras: {
          icon: modalVals.extras?.icon || "",
          desc: modalVals.extras?.desc || "",
        },
        __uid: makeUID(),
        menu_id: null,
      };
      newMenus = [...menuState, newItem];
    }

    setMenuState(newMenus);
    formik.setFieldValue("menu", newMenus);
    setActiveModal(null);
    setEditMenuItem({ item: null, path: [] });
  };

  /* ---------------- Data fetching ---------------- */
  const fetchMenuTypes = async () => {
    try {
      setMenuTypes((v) => ({ ...v, loading: true }));
      const result = await requestWithToken("GET", "/api/menutype");
      if (result?.status === 200) {
        setMenuTypes({ loading: false, data: result.data.data });
        setSelectedMenuType(result.data.data[0]?.slug || null);
      }
    } catch {
      setMenuTypes((v) => ({ ...v, loading: false, data: [] }));
    }
  };

  const fetchSelectedMenu = async (slug) => {
    try {
      setSelectedMenuData((v) => ({ ...v, loading: true }));
      const result = await requestWithToken(
        "GET",
        `/api/menu/navigation/list?menu_type_slug=${slug}`
      );
      if (result?.status === 200) {
        setSelectedMenuData({ loading: false, data: result.data.data });
        formik.setFieldValue(
          "primarybutton",
          result.data.data.primarybutton || { name: "", redirect_to: "" }
        );
        formik.setFieldValue(
          "secondarybutton",
          result.data.data.secondarybutton || { name: "", redirect_to: "" }
        );
        formik.setFieldValue("socialmedia", result.data.data.socialmedia || []);

        const withUids = ensureUIDs(result.data.data.menu || []);
        setMenuState(withUids);
        formik.setFieldValue("menu", withUids);
      }
      console.log("🔹 selectedMenuType =", slug);
      console.log("🔹 API response =", result?.data);

    } catch {
      setSelectedMenuData((v) => ({ ...v, loading: false, data: null }));
      formik.setFieldValue("primarybutton", { name: "", redirect_to: "" });
      formik.setFieldValue("secondarybutton", { name: "", redirect_to: "" });
      setMenuState([]);
      formik.setFieldValue("menu", []);
    }
  };

  const fetchPageList = async () => {
    try {
      setPageList((v) => ({ ...v, loading: true }));
      const result = await requestWithToken(
        "GET",
        "/api/page/navigation/menu/list?page=1&limit=100"
      );
      if (result?.status === 200) {
        const menudata = result.data.data?.list.map((page) => ({
          ...page,
          name: page.title || "",
        }));
        setPageList({
          loading: false,
          data: { ...result.data.data, list: menudata },
        });
      }
    } catch {
      setPageList((v) => ({ ...v, loading: false, data: [] }));
    }
  };

  useEffect(() => {
    fetchMenuTypes();
    fetchPageList();
  }, []);

  useEffect(() => {
    if (selectedMenuType) fetchSelectedMenu(selectedMenuType);
  }, [selectedMenuType]);

  return (
    <>
      <PageHeader pageTitle="Navigation">

          <Help
            title={helpPagesData.pages?.title || helpPagesData.menu?.title}
            message={helpPagesData.pages?.message || helpPagesData.menu?.message}
            items={helpPagesData.pages?.items || helpPagesData.menu?.items}
          />
          <button
            className="btn-black"
            type="submit"
            onClick={() => {
              formik.setFieldValue("menu", menuState);
              formik.handleSubmit();
            }}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Save Navigation"}
          </button>

      </PageHeader>

      <div className="page-container">
        <div className="theme-card no-shadow mb-3">
          <div className="p-2 ps-3 d-flex align-items-center">
            <div className="card-title">Select Menu to Edit</div>
            <div className="sel-box ms-auto">
              <select
                name="selmenu"
                id="selmenu"
                className="theme-select pointer"
                onChange={(e) => setSelectedMenuType(e.target.value)}
                value={selectedMenuType || ""}
              >
                {menuTypes.loading ? (
                  <option>Loading...</option>
                ) : menuTypes.data.length ? (
                  menuTypes.data.map((menu) => (
                    <option key={menu._id} value={menu.slug}>
                      {menu.name}
                    </option>
                  ))
                ) : (
                  <option>No Menu Types</option>
                )}
              </select>
            </div>
          </div>
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          collisionDetection={customCollisionDetection}
        >
          <div className="row">
            <div className="col-md-4">
              <div className="sticky-sec">
                <div className="theme-card">
                  <div className="p-3">
                    <div className="card-title mb-3">All Pages</div>
                    {pageList.loading ? (
                      <div className="text-center">
                        <Spinner size="sm" />
                      </div>
                    ) : filteredPages.length ? (
                      <DroppableAllPages isOver={overId === "ALL_PAGES"}>
                        <SortableContext
                          items={filteredPages.map((page) => `page-${page._id}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {filteredPages.map((page) => (
                            <DraggablePageItem key={page._id} page={page} />
                          ))}
                        </SortableContext>
                      </DroppableAllPages>
                    ) : (
                      <div>No pages available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="theme-card">
                <div className="p-3">
                  <div className="card-title mb-3">
                    {menuTypes?.data?.find((el) => el?.slug === selectedMenuType)
                      ?.name || ""}{" "}
                    Menu Structure
                  </div>

                  <div className="primary-btn-section mb-3">
                    <label className="item-label">Primary Button</label>
                    <div className="primery-btn-items multiple-col">
                      <input
                        type="text"
                        className="page-menu-item"
                        name="primarybutton.name"
                        value={formik.values.primarybutton?.name || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter button name"
                      />
                      <input
                        type="text"
                        className="page-menu-item"
                        name="primarybutton.redirect_to"
                        value={formik.values.primarybutton?.redirect_to || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter link"
                      />
                    </div>
                  </div>

                  <div className="primary-btn-section mb-3">
                    <label className="item-label">Secondary Button</label>
                    <div className="primery-btn-items multiple-col">
                      <input
                        type="text"
                        className="page-menu-item"
                        name="secondarybutton.name"
                        value={formik.values.secondarybutton?.name || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter button name"
                      />
                      <input
                        type="text"
                        className="page-menu-item"
                        name="secondarybutton.redirect_to"
                        value={formik.values.secondarybutton?.redirect_to || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter link"
                      />
                    </div>
                  </div>

                  <div className="primary-btn-section mb-3">
                    <label className="item-label">Custom Link</label>
                    <div className="primery-btn-items">
                      <button className="addbtn w-100" onClick={handleAddCustomLink}>
                        Add Custom Link
                        <img src={addIcon} alt="add" className="btn-icon ms-auto" />
                      </button>
                    </div>
                  </div>

                  <div className="pages-menu-section">
                    <label className="item-label">Menu Structure</label>
                    <DroppableMenuPages isOver={overId === "MENU_PAGES"}>
                      {menuState.length > 0 ? (
                        <DataSortableTree
                          allIteams={menuState}
                          setAllItems={(values) => {
                            setMenuState(values);
                            formik.setFieldValue("menu", values);
                          }}
                          onEditItem={handleEdit}
                          onRemoveItem={(id) => {
                            setMenuState(removeItemById(menuState, id));
                          }}
                        />
                      ) : (
                        <div className="text-center py-5 text-muted">
                          Drag pages here or add custom links to build your menu
                        </div>
                      )}
                    </DroppableMenuPages>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
                      Drag and drop pages from the left to add to the navigation
                      menu. Drag an item vertically to change the order, and
                      sideways to move across the hierarchy. Follow the (greyed
                      out) guide position and release to place the menu item
                      there.
                      <ul style={{ paddingLeft: "16px" }}>
                        <li>
                          ⁠You can create custom links for a dropdown parent
                          and/or linking an external page. For a dropdown
                          parent, keep the link as '#'.
                        </li>
                        <li>
                          ⁠Close the dropdowns (if any) for the best experience
                          while reordering the main menu items.
                        </li>
                      </ul>
                    </div>
                  </div>

                  {formik.values.socialmedia?.length > 0 && (
                    <div className="social-menu-section">
                      <label className="item-label">Social Media</label>
                      <div className="social-menu-items">
                        {formik.values.socialmedia.map((el, idx) => (
                          <div key={el.name || idx} className="social-menu-item">
                            <div className="social-icon">
                              <img
                                src={bucketUrl + el.extras?.icon || "/placeholder.svg"}
                                alt={el.name}
                              />
                            </div>
                            <input
                              type="url"
                              className="frm-input sm"
                              placeholder="URL"
                              value={el.redirect_to || ""}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "socialmedia",
                                  formik.values.socialmedia.map((link, i) =>
                                    i === idx ? { ...link, redirect_to: e.target.value } : link
                                  )
                                )
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DragOverlay>
            {draggedItem && draggedItemData ? (
              <div
                style={{
                  padding: "10px 14px",
                  backgroundColor: "#2563eb",
                  color: "white",
                  borderRadius: "6px",
                  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                  fontSize: "14px",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                  maxWidth: "300px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {draggedItemData}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <AddEditMenuItem
        show={activeModal === "editMenuItem"}
        onClose={() => {
          setActiveModal(null);
          setEditMenuItem({ item: null, path: [] });
        }}
        onSave={handleMenuItemModalSave}
        initialData={editMenuItem.item}
        isChild={editMenuItem?.item?.menu_id != null}
      />
    </>
  );
}
